import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { MaskedLines } from './fx/MaskedLines';
import { MagneticCTA } from './home/ui';

// ヘッダーナビと同順のサイトマップ（資料請求は製品ごとのページの入口 /documents）
// 3つの事業のLPと、コミュニティ LOCKED IN のLPは新しいタブで開く（2026-10-17・18 代表指示）
const sitemap = [
  { name: 'ホーム', path: '/' },
  { name: 'Trooper', href: '/trooper/' },
  { name: '内製化支援', href: '/enablement' },
  { name: 'AIコンサルティング・開発', href: '/cases' },
  { name: '会社概要', path: '/about' },
  { name: 'ニュース', path: '/news' },
  { name: '採用サイト', path: '/careers' },
  { name: 'コミュニティ LOCKED IN', href: '/community' },
  { name: '資料請求', path: '/documents' },
  { name: 'お問い合わせ', path: '/contact' },
];

const marqueeWords = [
  'ADVANCE HUMAN HISTORY',
  'Trooper',
  'Enablement',
  'Consulting',
  'Maximize Impact',
];

const metaStrip = ['Est. 2023', 'Tokyo', 'ROOTED IN UTOKYO', 'Boost Group'];

const Footer = () => {
  return (
    <footer className="bg-gray-950 text-paper">
      {/* マーキー: ゆっくり流れ、ホバーで停止 */}
      <div className="overflow-hidden border-b border-white/10 py-4" aria-hidden="true">
        <div className="fx-marquee flex w-max items-center gap-10 whitespace-nowrap pr-10">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex items-center gap-10">
              {marqueeWords.map((w) => (
                <React.Fragment key={`${dup}-${w}`}>
                  <span className="display-type text-[15px] tracking-[0.08em] text-paper/50">
                    {w}
                  </span>
                  <span className="h-1 w-1 rounded-full bg-live" />
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* CTAバンド */}
      <div className="px-6 py-20 md:px-10 md:py-28 lg:px-14 xl:pl-24 xl:pr-14">
        <div className="flex flex-col justify-between gap-12 lg:flex-row lg:items-end">
          <div>
            <MaskedLines
              as="h2"
              per="char"
              lines={['BUILD WHAT', 'MOVES THE WORLD']}
              className="display-type text-[11vw] text-paper md:text-7xl lg:text-[5.6vw] 2xl:text-[92px]"
              stagger={0.12}
            />
            <MaskedLines
              lines={[['まずはご相談から。', '課題に合わせて、始め方をご提案します。']]}
              className="mt-7 text-[15px] font-medium leading-relaxed tracking-display-ja text-gray-400 md:text-base"
              delay={0.3}
            />
          </div>
          <div className="flex shrink-0 flex-col gap-4 sm:flex-row">
            <MagneticCTA to="/contact" variant="live">
              お問い合わせ
            </MagneticCTA>
            <MagneticCTA to="/careers" variant="outline-paper" icon="up">
              採用サイト
            </MagneticCTA>
          </div>
        </div>
      </div>

      {/* メタストリップ */}
      <div className="grid grid-cols-2 border-y border-white/10 md:grid-cols-4">
        {metaStrip.map((m, i) => (
          <div
            key={m}
            className={`flex items-center justify-center border-white/10 px-3 py-4 text-center text-[10px] font-semibold uppercase tracking-label text-gray-500 md:text-[11px] ${
              ['', 'border-l', 'max-md:border-t md:border-l', 'border-l max-md:border-t'][i]
            }`}
          >
            {m}
          </div>
        ))}
      </div>

      {/* インフォ */}
      <div className="mx-auto grid grid-cols-1 gap-12 px-6 py-14 md:grid-cols-12 md:gap-8 md:px-10 lg:px-14 xl:pl-24 xl:pr-14">
        <div className="md:col-span-5">
          <img src="/icon/SALT2_logo_black.png" alt="SALT2" className="h-7 w-auto invert" />
          <p className="mt-6 max-w-md text-sm leading-relaxed text-gray-400">
            AIをつくる力と、企業の仕事に届ける力。
            <span className="inline-block">その両方を持つ。</span>
          </p>
        </div>

        <div className="md:col-span-4">
          <div className="mb-5 text-[11px] font-semibold uppercase tracking-label text-gray-500">
            Sitemap
          </div>
          <ul className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-gray-400">
            {sitemap.map((item) => (
              <li key={item.path ?? item.href}>
                {item.href ? (
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 transition-colors hover:text-paper"
                  >
                    {item.name}
                    <ArrowUpRight size={12} aria-hidden="true" />
                  </a>
                ) : (
                  <Link to={item.path} className="transition-colors hover:text-paper">
                    {item.name}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="mb-5 text-[11px] font-semibold uppercase tracking-label text-gray-500">
            Contact
          </div>
          <ul className="space-y-3 text-sm text-gray-400">
            <li>
              <Link to="/contact" className="transition-colors hover:text-paper">
                お問い合わせフォーム
              </Link>
            </li>
            <li className="leading-relaxed">
              東京都港区麻布台1丁目8-10
              <br />
              麻布偕成ビル3階
            </li>
            <li>
              <a
                href="https://www.boostconsulting.co.jp/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 transition-colors hover:text-paper"
              >
                Boost Consulting
                <ArrowUpRight size={13} />
              </a>
            </li>
            <li>
              <a
                href="https://events.salt-2.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 transition-colors hover:text-paper"
              >
                SALT2 Bootcamp
                <ArrowUpRight size={13} />
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="flex flex-col gap-2 px-6 py-5 sm:flex-row sm:items-center sm:justify-between md:px-10 lg:px-14 xl:pl-24 xl:pr-14">
          <p className="text-[11px] uppercase tracking-label text-gray-500">© 2026 SALT2, Inc.</p>
          <p className="text-[11px] uppercase tracking-label text-gray-500">
            Product / Enablement / Consulting — Tokyo
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
